import { ContentDraftProvider } from "../../hooks/useContentDraftGuard";
import { ArticleTools } from "../../components/marketing/ArticleTools";
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
  Snackbar,
  Modal,
  ModalDialog,
  ModalClose,
  AspectRatio,
} from "@mui/joy";
import {
  ArrowLeft as BackIcon,
  UploadSimple as PublishIcon,
  Image as ImageIcon,
  Trash as DeleteIcon,
} from "@phosphor-icons/react";
import { ArticleEditor } from "../../components/marketing/ArticleEditor";
import { MediaLibrary } from "../../components/marketing/MediaLibrary";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";

interface MediaFile {
  id: string | number;
  name: string;
  file: string;
  thumbnail?: string;
  file_type: "image" | "video";
}

interface ArticleData {
  id: number;
  title: string;
  status: string;
  meta_title?: string;
  meta_description?: string;
  keywords?: string;
  featured_image?: number | null;
  featured_image_url?: string | null;
}

const ArticleViewContent: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    color: "success" | "danger";
  }>({
    open: false,
    message: "",
    color: "success",
  });
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  // Featured image mutation
  const featuredImageMutation = useMutation({
    mutationFn: async (imageId: number | null) => {
      return api.patch(`/api/marketing/articles/${articleId}/`, {
        featured_image: imageId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["article", articleId] });
      setSnackbar({
        open: true,
        message: "Featured image updated!",
        color: "success",
      });
      setShowMediaLibrary(false);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "Failed to update featured image",
        color: "danger",
      });
    },
  });

  const handleImageSelect = (media: MediaFile) => {
    if (media.file_type !== "image") {
      setSnackbar({
        open: true,
        message: "Choose an image for this article.",
        color: "danger",
      });
      return;
    }
    const [source, id] = String(media.id).includes("_")
      ? String(media.id).split("_")
      : ["marketing", String(media.id)];
    void api
      .post(`/api/marketing/articles/${articleId}/featured-image/`, {
        source,
        id: Number(id),
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["article", articleId] });
        queryClient.invalidateQueries({ queryKey: ["article-preview"] });
        setShowMediaLibrary(false);
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: "Could not attach this image. Please try again.",
          color: "danger",
        }),
      );
  };

  const handleRemoveImage = () => {
    featuredImageMutation.mutate(null);
  };

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/marketing/articles/${articleId}/publish/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["article", articleId] });
      queryClient.invalidateQueries({ queryKey: ["content-stats"] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      setSnackbar({
        open: true,
        message: "Article published successfully!",
        color: "success",
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || "Failed to publish article",
        color: "danger",
      });
    },
  });

  // Fetch article details
  const { data: article, isLoading } = useQuery({
    queryKey: ["article", articleId],
    queryFn: async () => {
      const response = await api.get<ArticleData>(
        `/api/marketing/articles/${articleId}/`,
      );
      return response.data;
    },
    enabled: !!articleId,
  });

  const handleBack = () => {
    navigate("/marketing/content");
  };

  const handleClose = () => {
    navigate("/marketing/content");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography level="h3">Article not found</Typography>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Content Hub
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 3,
          gap: 2,
        }}
      >
        <IconButton onClick={handleBack} variant="outlined">
          <BackIcon />
        </IconButton>
        <Typography
          level="h2"
          sx={{
            flex: { xs: "1 1 calc(100% - 60px)", md: 1 },
            minWidth: 0,
            fontSize: { xs: "1.5rem", md: "2rem" },
            overflowWrap: "anywhere",
          }}
        >
          {article.title}
        </Typography>
        <Chip
          size="sm"
          color={
            article.status === "published"
              ? "success"
              : article.status === "content_done"
                ? "primary"
                : "warning"
          }
        >
          {article.status === "published"
            ? "Published"
            : article.status === "content_done"
              ? "Ready to Publish"
              : article.status}
        </Chip>
        {article.status === "content_done" && (
          <Button
            color="success"
            startDecorator={<PublishIcon />}
            onClick={() => publishMutation.mutate()}
            loading={publishMutation.isPending}
          >
            Publish
          </Button>
        )}
      </Box>

      <ArticleTools key={articleId} articleId={Number(articleId)} />

      {/* Main Content Area with Sidebar */}
      <Grid container spacing={3}>
        {/* Article Editor - Main Content */}
        <Grid xs={12} lg={9}>
          <Card variant="outlined">
            <CardContent>
              <ArticleEditor
                articleId={parseInt(articleId!)}
                onClose={handleClose}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid xs={12} lg={3}>
          {/* Featured Image Card */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ImageIcon style={{ marginRight: 8 }} />
                <Typography level="h4">Featured Image</Typography>
              </Box>

              {article.featured_image_url ? (
                <>
                  <AspectRatio
                    ratio="16/9"
                    sx={{ mb: 2, borderRadius: "md", overflow: "hidden" }}
                  >
                    <img
                      src={article.featured_image_url}
                      alt="Featured"
                      style={{ objectFit: "cover" }}
                    />
                  </AspectRatio>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="primary"
                      onClick={() => setShowMediaLibrary(true)}
                      sx={{ flex: 1 }}
                    >
                      Change
                    </Button>
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="danger"
                      onClick={handleRemoveImage}
                      loading={featuredImageMutation.isPending}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    py: 4,
                    textAlign: "center",
                    background: "background.level1",
                    borderRadius: "md",
                    border: "2px dashed",
                    borderColor: "neutral.300",
                  }}
                >
                  <ImageIcon
                    size={48}
                    color="var(--joy-palette-neutral-400)"
                    style={{ marginBottom: 8 }}
                  />
                  <Typography
                    level="body-sm"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    No featured image set
                  </Typography>
                  <Button
                    size="sm"
                    variant="soft"
                    onClick={() => setShowMediaLibrary(true)}
                  >
                    Select Image
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        color={snackbar.color}
      >
        {snackbar.message}
      </Snackbar>

      {/* Media Library Modal */}
      <Modal open={showMediaLibrary} onClose={() => setShowMediaLibrary(false)}>
        <ModalDialog
          size="lg"
          sx={{
            width: "90vw",
            maxWidth: 1200,
            height: "80vh",
            overflow: "hidden",
          }}
        >
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Select Featured Image
          </Typography>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <MediaLibrary
              selectionMode={true}
              onSelect={handleImageSelect}
              selectedMedia={null}
            />
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
export const ArticleView = () => (
  <ContentDraftProvider>
    <ArticleViewContent />
  </ContentDraftProvider>
);
